import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import {
  Mail, Phone, Settings, CheckCircle, XCircle, Server,
  RefreshCw, Activity, AlertCircle, Loader2, Send, TestTube,
  BarChart3, Zap
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Provider info for display
const EMAIL_PROVIDERS = {
  resend: { name: 'Resend', color: 'bg-blue-500', quota: 3000 },
  mailjet: { name: 'Mailjet', color: 'bg-purple-500', quota: 6000 },
  sendgrid: { name: 'SendGrid', color: 'bg-indigo-500', quota: 3000 },
  aws_ses: { name: 'AWS SES', color: 'bg-orange-500', quota: 62000 }
};

const OTPAdminSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingProvider, setTestingProvider] = useState(null);
  
  // Settings State
  const [otpSettings, setOtpSettings] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);
  const [emailUsage, setEmailUsage] = useState(null);
  const [providerStatus, setProviderStatus] = useState({});
  
  // Test Email
  const [testEmail, setTestEmail] = useState('');

  useEffect(() => {
    fetchAllSettings();
  }, []);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/otp/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setOtpSettings(response.data.otp_settings);
      setEmailSettings(response.data.email_settings);
      setEmailUsage(response.data.email_usage);
      setProviderStatus(response.data.provider_status);
    } catch (err) {
      console.error('Failed to fetch OTP settings:', err);
      toast({
        title: "Error",
        description: "Failed to load OTP settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/otp/admin/settings`, {
        otp_settings: otpSettings,
        email_settings: emailSettings
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Settings Saved",
        description: "OTP settings have been updated successfully",
      });
    } catch (err) {
      console.error('Failed to save settings:', err);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const testEmailProvider = async (provider) => {
    if (!testEmail) {
      toast({
        title: "Error",
        description: "Please enter a test email address",
        variant: "destructive"
      });
      return;
    }
    
    setTestingProvider(provider);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/otp/admin/test-email?provider=${provider}&test_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast({
          title: "Test Email Sent!",
          description: `Email sent via ${EMAIL_PROVIDERS[provider]?.name || provider}`,
        });
      } else {
        throw new Error(response.data.error);
      }
    } catch (err) {
      toast({
        title: "Test Failed",
        description: err.response?.data?.detail || err.message || "Failed to send test email",
        variant: "destructive"
      });
    } finally {
      setTestingProvider(null);
    }
  };

  const toggleOTPEnabled = () => {
    setOtpSettings(prev => ({ ...prev, enabled: !prev.enabled }));
  };

  const toggleEmailOTP = () => {
    setOtpSettings(prev => ({ ...prev, email_otp_enabled: !prev.email_otp_enabled }));
  };

  const toggleSMSOTP = () => {
    setOtpSettings(prev => ({ ...prev, sms_otp_enabled: !prev.sms_otp_enabled }));
  };

  const toggleRequiredFor = (action) => {
    setOtpSettings(prev => ({
      ...prev,
      required_for: {
        ...prev.required_for,
        [action]: !prev.required_for?.[action]
      }
    }));
  };

  const toggleProvider = (provider) => {
    setEmailSettings(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: {
          ...prev.providers?.[provider],
          enabled: !prev.providers?.[provider]?.enabled
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="h-6 w-6 text-blue-600" />
            OTP & Email Settings
          </h2>
          <p className="text-gray-500">Manage email verification, OTP settings, and email providers</p>
        </div>
        <Button onClick={saveSettings} disabled={saving}>
          {saving ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving...</>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>

      {/* Master Toggle */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${otpSettings?.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {otpSettings?.enabled ? (
                  <CheckCircle className="h-6 w-6 text-green-600" />
                ) : (
                  <XCircle className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-lg">OTP Verification System</h3>
                <p className="text-sm text-gray-500">Enable or disable OTP verification across the platform</p>
              </div>
            </div>
            <Switch
              checked={otpSettings?.enabled || false}
              onCheckedChange={toggleOTPEnabled}
            />
          </div>
        </CardContent>
      </Card>

      {/* OTP Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            OTP Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email & SMS Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="font-medium">Email OTP</p>
                  <p className="text-sm text-gray-500">Send verification codes via email</p>
                </div>
              </div>
              <Switch
                checked={otpSettings?.email_otp_enabled || false}
                onCheckedChange={toggleEmailOTP}
                disabled={!otpSettings?.enabled}
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">SMS OTP</p>
                  <p className="text-sm text-gray-500">Send verification codes via SMS</p>
                </div>
              </div>
              <Switch
                checked={otpSettings?.sms_otp_enabled || false}
                onCheckedChange={toggleSMSOTP}
                disabled={!otpSettings?.enabled}
              />
            </div>
          </div>

          {/* OTP Settings */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>OTP Length</Label>
              <Input
                type="number"
                min="4"
                max="8"
                value={otpSettings?.otp_length || 6}
                onChange={(e) => setOtpSettings(prev => ({ ...prev, otp_length: parseInt(e.target.value) }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Expiry (minutes)</Label>
              <Input
                type="number"
                min="1"
                max="30"
                value={otpSettings?.expiry_minutes || 10}
                onChange={(e) => setOtpSettings(prev => ({ ...prev, expiry_minutes: parseInt(e.target.value) }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Max Attempts</Label>
              <Input
                type="number"
                min="1"
                max="10"
                value={otpSettings?.max_attempts || 3}
                onChange={(e) => setOtpSettings(prev => ({ ...prev, max_attempts: parseInt(e.target.value) }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Required For */}
          <div>
            <Label className="mb-3 block">Require OTP For:</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'registration', label: 'Registration' },
                { key: 'password_reset', label: 'Password Reset' },
                { key: 'sensitive_actions', label: 'Sensitive Actions' },
                { key: 'login_2fa', label: 'Login 2FA' }
              ].map(item => (
                <div
                  key={item.key}
                  onClick={() => toggleRequiredFor(item.key)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    otpSettings?.required_for?.[item.key]
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {otpSettings?.required_for?.[item.key] ? (
                      <CheckCircle className="h-4 w-4 text-blue-600" />
                    ) : (
                      <XCircle className="h-4 w-4 text-gray-400" />
                    )}
                    <span className="text-sm font-medium">{item.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Providers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Email Providers
          </CardTitle>
          <CardDescription>
            Configure and manage email service providers. Emails are sent based on priority order and quota availability.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Test Email Input */}
          <div className="flex gap-2 mb-6">
            <Input
              type="email"
              placeholder="Enter test email address"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="max-w-xs"
            />
          </div>

          {/* Provider Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(EMAIL_PROVIDERS).map(([key, provider]) => {
              const status = providerStatus[key];
              const usage = emailUsage?.providers?.[key];
              const settings = emailSettings?.providers?.[key];
              const isConfigured = status?.configured;
              const isEnabled = settings?.enabled !== false;
              
              return (
                <Card key={key} className={`border-2 ${isEnabled && isConfigured ? 'border-green-200' : 'border-gray-200'}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${provider.color} flex items-center justify-center`}>
                          <Mail className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold">{provider.name}</h4>
                          <div className="flex items-center gap-2">
                            <Badge variant={isConfigured ? 'default' : 'secondary'} className="text-xs">
                              {isConfigured ? 'Configured' : 'Not Configured'}
                            </Badge>
                            {isConfigured && (
                              <Badge variant={isEnabled ? 'default' : 'outline'} className="text-xs">
                                {isEnabled ? 'Enabled' : 'Disabled'}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <Switch
                        checked={isEnabled && isConfigured}
                        onCheckedChange={() => toggleProvider(key)}
                        disabled={!isConfigured}
                      />
                    </div>

                    {/* Usage Stats */}
                    {usage && (
                      <div className="mb-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-500">Usage this month</span>
                          <span className="font-medium">{usage.used} / {usage.quota}</span>
                        </div>
                        <Progress value={usage.percentage} className="h-2" />
                        <p className="text-xs text-gray-400 mt-1">{usage.remaining} remaining</p>
                      </div>
                    )}

                    {/* Test Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => testEmailProvider(key)}
                      disabled={!isConfigured || !isEnabled || testingProvider === key}
                      className="w-full"
                    >
                      {testingProvider === key ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Testing...</>
                      ) : (
                        <><Send className="mr-2 h-4 w-4" /> Send Test Email</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Usage Overview */}
      {emailUsage && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Email Usage Overview - {emailUsage.month}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{emailUsage.total_sent}</p>
                <p className="text-sm text-gray-500">Emails Sent</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{emailUsage.total_quota}</p>
                <p className="text-sm text-gray-500">Total Quota</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg">
                <p className="text-3xl font-bold text-purple-600">
                  {emailUsage.total_quota - emailUsage.total_sent}
                </p>
                <p className="text-sm text-gray-500">Remaining</p>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <p className="text-3xl font-bold text-orange-600">
                  {((emailUsage.total_sent / emailUsage.total_quota) * 100).toFixed(1)}%
                </p>
                <p className="text-sm text-gray-500">Used</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OTPAdminSettings;
